import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Button, Input, Modal } from "@vv/ui";
import { useState } from "react";
import { ALLOWED_INVITE_EMAIL_DOMAIN } from "../domain/user.types";

export interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (email: string, displayName: string) => Promise<void>;
}

function emailError(email: string): string | null {
  if (!email.trim()) return null;
  const domain = email.trim().toLowerCase().split("@")[1];
  if (domain !== ALLOWED_INVITE_EMAIL_DOMAIN) {
    return `Must be an @${ALLOWED_INVITE_EMAIL_DOMAIN} address`;
  }
  return null;
}

export function InviteUserModal({ open, onClose, onConfirm }: InviteUserModalProps) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  const validationError = emailError(email);

  async function handleConfirm() {
    setSaving(true);
    try {
      await onConfirm(email.trim().toLowerCase(), displayName.trim());
      setEmail("");
      setDisplayName("");
    } catch {
      // Already toasted by the caller - swallow so it doesn't also surface
      // as an unhandled promise rejection.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite user"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            loading={saving}
            disabled={email.trim().length === 0 || Boolean(validationError)}
            onClick={handleConfirm}
          >
            Invite
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          They&apos;ll sign in with their Microsoft account - no password is set here.
        </Typography>
        <Input
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={Boolean(validationError)}
          helperText={validationError ?? `Must be an @${ALLOWED_INVITE_EMAIL_DOMAIN} address`}
          required
          autoFocus
        />
        <Input
          label="Display name (optional)"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </Stack>
    </Modal>
  );
}
